import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import AdminShell from '../components/admin/AdminShell';
import {
  DAILY_GROUP_OPTIONS,
  DAILY_RANGE_PRESETS,
  dailyRideLifecycleBucket,
  fetchDailyRideRequests,
  formatPeriodLabel,
  isInstantDailyRide,
  resolveRangePreset,
  summarizeInstantDailyRides,
} from '../lib/daily-rides-dashboard';
import {
  formatRideAddress,
  formatRideDateParts,
  formatRideFare,
  formatRidePhone,
  getRideDisplayMarket,
  rideStatusLabel,
} from '../lib/rides';
import { useAdminRidesFeed } from '../hooks/useAdminRidesFeed';
import { isSupabaseConfigured, supabase } from '../lib/supabase';

const TRIP_PAGE_SIZES = [10, 15, 25];

const LIST_FILTER_LABELS = {
  active: 'active now',
  completed: 'completed',
  cancelled: 'cancelled',
  all: 'all in range',
};
const PERIOD_PAGE_SIZE = 10;

function buildPageList(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const pages = new Set([1, total, current, current - 1, current + 1]);
  const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const out = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) out.push('…');
    out.push(p);
  });
  return out;
}

function DailyPagination({
  totalItems,
  pageSize,
  currentPage,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions,
  itemLabel = 'items',
}) {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const page = Math.min(Math.max(1, currentPage), totalPages);
  const rangeStart = totalItems ? (page - 1) * pageSize + 1 : 0;
  const rangeEnd = Math.min(page * pageSize, totalItems);
  const pageList = buildPageList(page, totalPages);

  return (
    <footer className="rides-panel__footer daily-dash__pager">
      <p className="rides-panel__summary">
        {totalItems === 0 ? (
          `0 ${itemLabel}`
        ) : (
          <>
            Showing <strong>{rangeStart}</strong>–<strong>{rangeEnd}</strong> of{' '}
            <strong>{totalItems}</strong> {itemLabel}
          </>
        )}
      </p>

      {onPageSizeChange ? (
        <label className="rides-panel__page-size">
          <span>Per page</span>
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
          >
            {pageSizeOptions.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      <nav className="rides-panel__pager" aria-label="Pagination">
        <button
          type="button"
          className="rides-panel__page-btn"
          disabled={page <= 1}
          onClick={() => onPageChange(1)}
        >
          First
        </button>
        <button
          type="button"
          className="rides-panel__page-btn"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          Prev
        </button>
        <div className="rides-panel__page-numbers">
          {pageList.map((item, index) =>
            item === '…' ? (
              <span key={`gap-${index}`} className="rides-panel__ellipsis" aria-hidden="true">
                …
              </span>
            ) : (
              <button
                key={item}
                type="button"
                className={`rides-panel__page-num ${
                  item === page ? 'rides-panel__page-num--active' : ''
                }`}
                aria-current={item === page ? 'page' : undefined}
                onClick={() => onPageChange(item)}
              >
                {item}
              </button>
            )
          )}
        </div>
        <button
          type="button"
          className="rides-panel__page-btn"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
        >
          Next
        </button>
        <button
          type="button"
          className="rides-panel__page-btn"
          disabled={page >= totalPages}
          onClick={() => onPageChange(totalPages)}
        >
          Last
        </button>
      </nav>
    </footer>
  );
}

function AdminDailyRidesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sessionChecked, setSessionChecked] = useState(false);
  const [user, setUser] = useState(null);

  const rangePreset = searchParams.get('range') || '30d';
  const groupBy = searchParams.get('group') || 'day';
  const listFilter = searchParams.get('view') || 'active';
  const tripPage = Math.max(1, Number(searchParams.get('tp') || 1));
  const tripPageSize = TRIP_PAGE_SIZES.includes(Number(searchParams.get('ts')))
    ? Number(searchParams.get('ts'))
    : 15;
  const periodPage = Math.max(1, Number(searchParams.get('pp') || 1));

  const updateParams = useCallback(
    (patch) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        Object.entries(patch).forEach(([key, value]) => {
          if (value === '' || value == null) next.delete(key);
          else next.set(key, String(value));
        });
        return next;
      });
    },
    [setSearchParams]
  );

  useEffect(() => {
    if (!isSupabaseConfigured || !supabase) {
      setSessionChecked(true);
      return undefined;
    }

    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      const nextUser = data.session?.user || null;
      setUser(nextUser);
      setSessionChecked(true);
      if (!nextUser) navigate('/admin/login', { replace: true });
    });

    const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
      const nextUser = session?.user || null;
      setUser(nextUser);
      if (!nextUser) navigate('/admin/login', { replace: true });
    });

    return () => {
      active = false;
      authListener.subscription.unsubscribe();
    };
  }, [navigate]);

  const { start: rangeStart, end: rangeEnd } = useMemo(
    () => resolveRangePreset(rangePreset),
    [rangePreset]
  );

  const fetchDaily = useCallback(() => {
    const limit =
      rangePreset === 'today' ? 250 : rangePreset === '7d' ? 450 : rangePreset === '90d' ? 900 : 650;
    return fetchDailyRideRequests(supabase, {
      requestedSince: rangeStart,
      requestedBefore: rangeEnd,
      limit,
    });
  }, [rangePreset, rangeStart, rangeEnd]);

  const acceptDailyAppRide = useCallback(
    (row) => String(row?.product || '').toLowerCase() === 'daily',
    []
  );

  const { rides, loading, error, refresh, liveConnected, updatedAt } = useAdminRidesFeed({
    enabled: Boolean(user),
    fetchRides: fetchDaily,
    acceptAppRide: acceptDailyAppRide,
  });

  const safeGroup = DAILY_GROUP_OPTIONS.some((g) => g.value === groupBy) ? groupBy : 'day';

  const { instant, snapshot, periods } = useMemo(
    () =>
      summarizeInstantDailyRides(rides, {
        rangeStart,
        rangeEnd,
        groupBy: safeGroup,
      }),
    [rides, rangeStart, rangeEnd, safeGroup]
  );

  const filteredTrips = useMemo(() => {
    const filtered = instant.filter((ride) => {
      const lifecycle = dailyRideLifecycleBucket(ride);
      if (listFilter === 'active') return lifecycle === 'active';
      if (listFilter === 'completed') return lifecycle === 'completed';
      if (listFilter === 'cancelled') return lifecycle === 'cancelled';
      return true;
    });
    filtered.sort(
      (a, b) => Date.parse(b.created_at || 0) - Date.parse(a.created_at || 0)
    );
    return filtered;
  }, [instant, listFilter]);

  const tripTotalPages = Math.max(1, Math.ceil(filteredTrips.length / tripPageSize));
  const safeTripPage = Math.min(tripPage, tripTotalPages);
  const tripRows = useMemo(() => {
    const start = (safeTripPage - 1) * tripPageSize;
    return filteredTrips.slice(start, start + tripPageSize);
  }, [filteredTrips, safeTripPage, tripPageSize]);
  const tripRangeStart = filteredTrips.length ? (safeTripPage - 1) * tripPageSize + 1 : 0;
  const tripRangeEnd = Math.min(safeTripPage * tripPageSize, filteredTrips.length);
  const tripPageList = buildPageList(safeTripPage, tripTotalPages);
  const listFilterLabel = LIST_FILTER_LABELS[listFilter] || listFilter;

  const periodTotalPages = Math.max(1, Math.ceil(periods.length / PERIOD_PAGE_SIZE));
  const safePeriodPage = Math.min(periodPage, periodTotalPages);
  const periodRows = useMemo(() => {
    const start = (safePeriodPage - 1) * PERIOD_PAGE_SIZE;
    return periods.slice(start, start + PERIOD_PAGE_SIZE);
  }, [periods, safePeriodPage]);

  const tabCounts = useMemo(() => {
    const counts = { active: 0, completed: 0, cancelled: 0, all: instant.length };
    instant.forEach((ride) => {
      const lifecycle = dailyRideLifecycleBucket(ride);
      counts[lifecycle] += 1;
    });
    return counts;
  }, [instant]);

  const scheduledDailyCount = useMemo(
    () => rides.filter((r) => !isInstantDailyRide(r)).length,
    [rides]
  );

  const rangeLabel =
    DAILY_RANGE_PRESETS.find((p) => p.value === rangePreset)?.label || 'Last 30 days';

  const logout = async () => {
    await supabase?.auth.signOut();
    navigate('/admin/login', { replace: true });
  };

  if (!sessionChecked) {
    return <div className="admin-loading">Checking admin session…</div>;
  }
  if (!user) return null;

  return (
    <AdminShell
      userEmail={user.email}
      active="instant"
      onLogout={logout}
      actions={
        <button
          type="button"
          className="btn btn--ghost-dark"
          onClick={() => refresh({ showSpinner: true })}
          disabled={loading}
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      }
    >
      <div className="rides-page daily-dash instant-rides">
        <header className="admin-page-head admin-page-head--compact">
          <h1>
            Instant rides{' '}
            <span className="admin-page-head__hint">
              (Live city trips from the Saarthi app — tap a row for trip details)
            </span>
          </h1>
          {liveConnected ? (
            <p className="admin-live-pill" role="status">
              Live updates on
              {updatedAt
                ? ` · synced ${updatedAt.toLocaleTimeString('en-IN', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}`
                : ''}
            </p>
          ) : null}
        </header>

        <div className="instant-kpis" aria-label="Trip totals — click to filter list">
          {[
            { value: 'active', label: 'Active now', count: tabCounts.active, tone: 'active' },
            { value: 'completed', label: 'Completed', count: tabCounts.completed, tone: 'completed' },
            { value: 'cancelled', label: 'Cancelled', count: tabCounts.cancelled, tone: 'cancelled' },
            { value: 'all', label: 'All in range', count: tabCounts.all, tone: 'all' },
          ].map((kpi) => (
            <button
              key={kpi.value}
              type="button"
              className={`instant-kpi instant-kpi--${kpi.tone} ${
                listFilter === kpi.value ? 'instant-kpi--selected' : ''
              }`}
              aria-pressed={listFilter === kpi.value}
              onClick={() =>
                updateParams({
                  view: kpi.value === 'active' ? '' : kpi.value,
                  tp: 1,
                })
              }
            >
              <span className="instant-kpi__value">{kpi.count}</span>
              <span className="instant-kpi__label">{kpi.label}</span>
            </button>
          ))}
        </div>

        {snapshot.active > 0 ? (
          <p className="instant-callout" role="status">
            <strong>{snapshot.active}</strong> trip{snapshot.active === 1 ? '' : 's'} need attention
            now — switch to <em>Active now</em> to review trips in progress.
          </p>
        ) : null}

        {error ? <div className="form-alert form-alert--error admin-error">{error}</div> : null}

        <section className="instant-panel instant-panel--table">
          <div className="instant-panel__toolbar admin-drivers-toolbar">
            <div className="instant-panel__toolbar-main">
              <h2>Trips</h2>
              <p>
                Showing <strong>{rangeLabel}</strong>
                {scheduledDailyCount > 0
                  ? ` · ${scheduledDailyCount} scheduled daily excluded`
                  : ''}
              </p>
            </div>
            <div className="instant-panel__toolbar-actions">
              <div className="instant-panel__chips" role="group" aria-label="Date range">
                {DAILY_RANGE_PRESETS.map((item) => (
                  <button
                    key={item.value}
                    type="button"
                    className={`instant-chip ${rangePreset === item.value ? 'instant-chip--active' : ''}`}
                    aria-pressed={rangePreset === item.value}
                    onClick={() => updateParams({ range: item.value, pp: 1, tp: 1 })}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
              <label className="driver-search__page">
                <span>Show</span>
                <select
                  value={tripPageSize}
                  onChange={(e) =>
                    updateParams({ ts: Number(e.target.value), tp: 1 })
                  }
                  aria-label="Rows per page"
                >
                  {TRIP_PAGE_SIZES.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
                <span>per page</span>
              </label>
            </div>
          </div>

          {loading ? (
            <p className="admin-empty instant-panel__empty">Loading instant trips…</p>
          ) : filteredTrips.length === 0 ? (
            <p className="admin-empty instant-panel__empty">
              No trips for this status and date range. Try <strong>All in range</strong> or a wider
              period.
            </p>
          ) : (
            <>
              <div className="instant-table-wrap">
                <table className="instant-trips-table">
                  <thead>
                    <tr>
                      <th scope="col">Rider</th>
                      <th scope="col">Route</th>
                      <th scope="col">Status</th>
                      <th scope="col">Booked</th>
                      <th scope="col">Phone</th>
                      <th scope="col">Fare</th>
                      <th scope="col">Driver</th>
                      <th scope="col">
                        <span className="sr-only">Open</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {tripRows.map((ride) => {
                      const when = formatRideDateParts(ride.created_at);
                      const market = getRideDisplayMarket(ride);
                      const pickup = formatRideAddress(ride.pickup_address);
                      const drop = formatRideAddress(ride.dropoff_address);
                      return (
                        <tr
                          key={ride.id}
                          className="instant-trips-table__row admin-table-row"
                          tabIndex={0}
                          onClick={() =>
                            navigate(`/admin/rides/app/${encodeURIComponent(ride.id)}`)
                          }
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              navigate(`/admin/rides/app/${encodeURIComponent(ride.id)}`);
                            }
                          }}
                        >
                          <td>
                            <strong>{ride.customer_name || 'Rider'}</strong>
                            <span className="instant-trips-table__id">
                              {ride.id.slice(0, 8)}…
                            </span>
                          </td>
                          <td className="instant-trips-table__route">
                            <span title={pickup}>{pickup}</span>
                            <span className="instant-trips-table__arrow" aria-hidden="true">
                              →
                            </span>
                            <span title={drop}>{drop}</span>
                          </td>
                          <td>
                            <span className={`ride-pill ride-pill--${ride.boardStatus}`}>
                              {rideStatusLabel(ride)}
                            </span>
                          </td>
                          <td className="instant-trips-table__when">
                            {when.date}
                            <br />
                            {when.time}
                          </td>
                          <td>{formatRidePhone(ride.customer_phone, market)}</td>
                          <td>{formatRideFare(ride.fare, ride) || '—'}</td>
                          <td>{ride.assigned_driver_name || '—'}</td>
                          <td>
                            <Link
                              to={`/admin/rides/app/${encodeURIComponent(ride.id)}`}
                              className="instant-trips-table__link"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Open
                            </Link>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {filteredTrips.length > 0 ? (
              <footer className="admin-drivers__footer">
                <p className="admin-drivers__summary">
                  <>
                    Showing <strong>{tripRangeStart}</strong>–<strong>{tripRangeEnd}</strong> of{' '}
                    <strong>{filteredTrips.length}</strong> ({listFilterLabel}) · newest first
                  </>
                </p>
                <nav className="rides-panel__pager" aria-label="Instant trip pages">
                  <button
                    type="button"
                    className="rides-panel__page-btn"
                    disabled={safeTripPage <= 1}
                    onClick={() => updateParams({ tp: 1 })}
                  >
                    First
                  </button>
                  <button
                    type="button"
                    className="rides-panel__page-btn"
                    disabled={safeTripPage <= 1}
                    onClick={() => updateParams({ tp: safeTripPage - 1 })}
                  >
                    Prev
                  </button>
                  {tripPageList.map((item, index) =>
                    item === '…' ? (
                      <span key={`gap-${index}`} className="rides-panel__ellipsis">
                        …
                      </span>
                    ) : (
                      <button
                        key={item}
                        type="button"
                        className={`rides-panel__page-btn ${
                          item === safeTripPage ? 'rides-panel__page-btn--active' : ''
                        }`}
                        onClick={() => updateParams({ tp: item })}
                      >
                        {item}
                      </button>
                    )
                  )}
                  <button
                    type="button"
                    className="rides-panel__page-btn"
                    disabled={safeTripPage >= tripTotalPages}
                    onClick={() => updateParams({ tp: safeTripPage + 1 })}
                  >
                    Next
                  </button>
                  <button
                    type="button"
                    className="rides-panel__page-btn"
                    disabled={safeTripPage >= tripTotalPages}
                    onClick={() => updateParams({ tp: tripTotalPages })}
                  >
                    Last
                  </button>
                </nav>
              </footer>
              ) : null}
            </>
          )}
        </section>

        <details className="instant-panel instant-panel--muted instant-trends">
          <summary className="instant-trends__summary">
            <span>Trends by period</span>
            <span className="instant-trends__hint">Summary table for the selected date range</span>
          </summary>
          <div className="instant-trends__body">
            <div className="instant-panel__chips instant-panel__chips--sub" role="group" aria-label="Group by">
              {DAILY_GROUP_OPTIONS.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  className={`instant-chip instant-chip--sm ${safeGroup === item.value ? 'instant-chip--active' : ''}`}
                  onClick={() => updateParams({ group: item.value, pp: 1 })}
                >
                  {item.label}
                </button>
              ))}
            </div>
            {loading ? (
              <p className="admin-empty instant-panel__empty">Loading…</p>
            ) : periods.length === 0 ? (
              <p className="admin-empty instant-panel__empty">No data in this range.</p>
            ) : (
              <>
                <div className="daily-dash__table-wrap">
                  <table className="daily-dash__table instant-trends-table">
                    <thead>
                      <tr>
                        <th scope="col">Period</th>
                        <th scope="col">Active</th>
                        <th scope="col">Completed</th>
                        <th scope="col">Cancelled</th>
                        <th scope="col">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {periodRows.map((row) => (
                        <tr key={row.key}>
                          <td>{formatPeriodLabel(row.key, safeGroup)}</td>
                          <td>{row.active}</td>
                          <td>{row.completed}</td>
                          <td>{row.cancelled}</td>
                          <td>
                            <strong>{row.total}</strong>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <DailyPagination
                  totalItems={periods.length}
                  pageSize={PERIOD_PAGE_SIZE}
                  currentPage={safePeriodPage}
                  onPageChange={(p) => updateParams({ pp: p })}
                  itemLabel="periods"
                />
              </>
            )}
          </div>
        </details>
      </div>
    </AdminShell>
  );
}

export default AdminDailyRidesPage;
